import React, { useEffect, useState } from "react";
import { Bell, ChevronDown, LogOut, User as UserIcon } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, ROLE_LABEL } from "@/context/AuthContext";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { useNavigate } from "react-router-dom";

export default function Header({ appName, fy, setFy }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [fyList, setFyList] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/notifications");
        setNotifs(data || []);
      } catch { /* ignore */ }
    })();
    (async () => {
      try {
        const { data } = await api.get("/dashboard/available-fys");
        setFyList(data.fys || []);
        if (data.current && !fy) setFy(data.current);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unread = notifs.filter((n) => !n.read).length;
  const years = fyList.length ? fyList : [new Date().getFullYear()];

  const initials = (user?.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <header
      data-testid={TID.shell.header}
      className="h-16 sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-slate-200 flex items-center px-6 gap-4"
    >
      <div className="flex-1">
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Fiscal Year</div>
        <div className="font-heading text-lg font-bold text-slate-900 leading-tight">{appName || "SAMS"} Console</div>
      </div>

      <div className="hidden sm:block">
        <Select value={String(fy)} onValueChange={(v) => setFy(Number(v))}>
          <SelectTrigger data-testid={TID.shell.fySelect} className="w-[140px] h-9 rounded-md">
            <SelectValue placeholder="Fiscal Year" />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={String(y)}>FY {y} (Apr {y} – Mar {y + 1})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Popover>
        <PopoverTrigger asChild>
          <button data-testid={TID.shell.notifBell} className="relative h-9 w-9 grid place-items-center rounded-md border border-slate-200 hover:bg-slate-50 transition-colors" aria-label="Notifications">
            <Bell size={18} className="text-slate-700" />
            {unread > 0 && (
              <span data-testid={TID.shell.notifBadge} className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center">
                {unread}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0 bg-white">
          <div className="px-4 py-3 border-b border-slate-200">
            <div className="text-sm font-semibold text-slate-900">Notifications</div>
            <div className="text-xs text-slate-500">{unread} unread</div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifs.length === 0 && <div className="text-sm text-slate-500 px-4 py-6 text-center">No notifications</div>}
            {notifs.slice(0, 10).map((n) => (
              <div key={n.id} className={`px-4 py-3 border-b border-slate-100 text-sm ${n.read ? "opacity-70" : "bg-blue-50/40"}`}>
                <div className="font-semibold text-slate-900">{n.title}</div>
                <div className="text-slate-600 text-xs mt-0.5">{n.message}</div>
                <div className="text-[10px] text-slate-400 mt-1">{new Date(n.timestamp).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={TID.shell.userMenu} className="flex items-center gap-2 h-9 pl-1 pr-2 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors">
            <div className="h-7 w-7 rounded-full bg-slate-900 text-white grid place-items-center text-[11px] font-bold">
              {initials}
            </div>
            <div className="hidden md:block text-left">
              <div className="text-xs font-semibold text-slate-900 leading-tight max-w-[140px] truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 leading-tight">{ROLE_LABEL[user?.role] || user?.role}</div>
            </div>
            <ChevronDown size={14} className="text-slate-500" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-white w-56">
          <DropdownMenuLabel className="font-normal">
            <div className="text-sm font-semibold text-slate-900">{user?.name}</div>
            <div className="text-xs text-slate-500">{user?.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => nav("/account")}>
            <UserIcon size={14} className="mr-2" /> My Account
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid={TID.shell.logoutBtn} onClick={async () => { await logout(); nav("/login"); }}>
            <LogOut size={14} className="mr-2" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
